import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contractApi } from '../services/contractApi';
import type { Contract } from '../services/contractApi';
import { propertyApi } from '../services/propertyApi';
import { useAlert } from '../contexts/AlertContext';
import CccdScannerModal from './CccdScannerModal';
import type { CccdParsedData } from '../utils/cccdOcr';
import { normalizeDateForInput } from '../utils/cccdOcr';
import { normalizeVnPhone } from '../utils/cccdQr';
import { roomPayApi } from '../services/roomPayApi';

export interface ContractPropertyOption {
  id: number;
  title: string;
  price: number;
  room_number?: string;
  full_address?: string;
}

function formatPropertyDisplayAddress(option: ContractPropertyOption): string {
  const address =
    option.full_address?.trim() ||
    option.title?.trim() ||
    'Địa chỉ không có sẵn';
  const room = option.room_number?.trim();
  return room ? `${address} — Căn ${room}` : address;
}

export type AddContractModalMode = 'create' | 'request' | 'accept';

export interface ContractFormPreset {
  property_id?: number;
  tenant_name?: string;
  tenant_phone?: string;
  tenant_cccd?: string;
  tenant_date_of_birth?: string;
  tenant_address?: string;
  start_date?: string;
  rental_duration?: number | string;
  monthly_payment?: number;
}

interface AddContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Phòng đã chọn sẵn (từ product card — tùy chọn) */
  propertyId?: number;
  propertyTitle?: string;
  propertyPrice?: number;
  /** Danh sách phòng để chọn khi mở từ nút header */
  properties?: ContractPropertyOption[];
  /** create: tạo trực tiếp | request: gửi chủ nhà | accept: chủ nhà chấp nhận */
  mode?: AddContractModalMode;
  /** Mã phòng QR — bắt buộc khi mode=request */
  payRoomCode?: string;
  /** Dữ liệu điền sẵn (mode accept từ thông báo) */
  initialData?: ContractFormPreset;
  onContractAdded?: (contract: Contract) => void;
  /** Gọi sau khi gửi yêu cầu thành công (mode request) */
  onRequestSubmitted?: () => void;
  /** Mở modal quét CCCD ngay khi hiển thị (luồng yêu cầu thuê QR phòng) */
  autoOpenCccdScanner?: boolean;
}

const getTodayForDateInput = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AddContractModal: React.FC<AddContractModalProps> = ({
  isOpen,
  onClose,
  propertyId: presetPropertyId,
  propertyTitle: presetPropertyTitle,
  propertyPrice: presetPropertyPrice,
  properties = [],
  mode = 'create',
  payRoomCode,
  initialData,
  onContractAdded,
  onRequestSubmitted,
  autoOpenCccdScanner = false,
}) => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [selectedPropertyId, setSelectedPropertyId] = useState<number>(0);
  const [tenantName, setTenantName] = useState('');
  const [cccdNumber, setCccdNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [address, setAddress] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [rentalDuration, setRentalDuration] = useState('12');
  const [startDate, setStartDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [openNativeCameraOnScan, setOpenNativeCameraOnScan] = useState(false);
  const [openGalleryOnScan, setOpenGalleryOnScan] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const cccdAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      cccdAutoOpenedRef.current = false;
      setIsScannerOpen(false);
      setOpenNativeCameraOnScan(false);
      setOpenGalleryOnScan(false);
      return;
    }

    const presetPropertyFromData =
      initialData?.property_id && initialData.property_id > 0
        ? initialData.property_id
        : undefined;
    const initialId =
      presetPropertyId && presetPropertyId > 0
        ? presetPropertyId
        : presetPropertyFromData
          ? presetPropertyFromData
          : properties.length === 1
            ? properties[0].id
            : 0;
    setSelectedPropertyId(initialId);
    setTenantName(initialData?.tenant_name?.trim() || '');
    setCccdNumber(initialData?.tenant_cccd?.trim() || '');
    setDateOfBirth(initialData?.tenant_date_of_birth || '');
    setAddress(initialData?.tenant_address?.trim() || '');
    setTenantPhone(initialData?.tenant_phone?.trim() || '');
    setRentalDuration(
      initialData?.rental_duration != null
        ? String(initialData.rental_duration)
        : '12',
    );
    // Ngày bắt đầu chỉ chủ nhà chọn khi tạo/chấp nhận hợp đồng.
    setStartDate(mode === 'request' ? '' : getTodayForDateInput());
    setSubmitError(null);
    // Chỉ hydrate form khi mở modal. Không reset scanner/form khi trang reload
    // sau khi chụp ảnh (visibilitychange) — nếu không dữ liệu QR sẽ bị xóa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      cccdAutoOpenedRef.current = false;
      return;
    }
    if (autoOpenCccdScanner && !cccdAutoOpenedRef.current) {
      cccdAutoOpenedRef.current = true;
      setOpenGalleryOnScan(false);
      setOpenNativeCameraOnScan(true);
      setIsScannerOpen(true);
    }
  }, [isOpen, autoOpenCccdScanner]);

  const handleOpenCccdScanner = () => {
    setOpenGalleryOnScan(false);
    setOpenNativeCameraOnScan(true);
    setIsScannerOpen(true);
  };

  const handleCloseCccdScanner = () => {
    setIsScannerOpen(false);
    setOpenNativeCameraOnScan(false);
    setOpenGalleryOnScan(false);
  };

  const selectedFromList = properties.find((p) => p.id === selectedPropertyId);
  const activePropertyId =
    presetPropertyId && presetPropertyId > 0
      ? presetPropertyId
      : initialData?.property_id && initialData.property_id > 0
        ? initialData.property_id
        : selectedPropertyId;
  const activePropertyLabel =
    presetPropertyTitle?.trim() ||
    (selectedFromList ? formatPropertyDisplayAddress(selectedFromList) : '');
  const activePropertyPrice =
    presetPropertyPrice ??
    initialData?.monthly_payment ??
    selectedFromList?.price ??
    0;
  const showPropertyPicker = !presetPropertyId && !initialData?.property_id && properties.length > 0;
  const isRequestMode = mode === 'request';
  const isAcceptMode = mode === 'accept';
  const modalTitle = isRequestMode
    ? 'Yêu cầu thuê phòng'
    : isAcceptMode
      ? 'Chấp nhận yêu cầu thuê'
      : t('propertyListings.addContract.title');
  const submitLabel = isRequestMode
    ? 'Gửi chủ nhà'
    : isAcceptMode
      ? 'Chấp nhận'
      : t('propertyListings.addContract.submit');
  const submitIcon = isRequestMode ? 'fa-paper-plane' : isAcceptMode ? 'fa-check' : 'fa-plus';

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const focusPhoneField = (message: string) => {
    setSubmitError(message);
    window.setTimeout(() => {
      phoneInputRef.current?.focus();
      phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const requestErrorMessage = (error: unknown): string => {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (error instanceof Error && error.message) return error.message;
    return 'Không gửi được yêu cầu. Vui lòng thử lại.';
  };

  const applyParsedCccd = (parsed: CccdParsedData) => {
    if (parsed.name?.trim()) setTenantName(parsed.name.trim());
    if (parsed.cccd?.trim()) setCccdNumber(parsed.cccd.replace(/\D/g, '').trim());
    if (parsed.dateOfBirth?.trim()) {
      const normalizedDate = normalizeDateForInput(parsed.dateOfBirth.trim());
      if (normalizedDate) setDateOfBirth(normalizedDate);
    }
    if (parsed.address?.trim()) setAddress(parsed.address.trim());
    const phone = normalizeVnPhone(parsed.phone) || parsed.phone?.trim();
    if (phone) setTenantPhone(phone);

    const filledCount = [parsed.name, parsed.cccd, parsed.dateOfBirth, parsed.address, parsed.phone].filter(Boolean).length;
    if (filledCount > 0) {
      showAlert({
        type: 'success',
        title: `Đã tự động điền ${filledCount}/5 trường`,
        message: phone
          ? filledCount < 5
            ? 'Vui lòng kiểm tra và bổ sung các trường chưa đọc được.'
            : 'Thông tin người thuê đã được điền — bạn có thể chỉnh sửa trước khi gửi.'
          : 'QR CCCD không có số điện thoại. Hãy nhập SĐT rồi bấm Gửi chủ nhà.',
      });
    }
    if (!phone) {
      focusPhoneField('QR CCCD không có số điện thoại. Vui lòng nhập SĐT rồi bấm Gửi chủ nhà.');
    } else {
      setSubmitError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!tenantName.trim()) {
      const message = t('propertyListings.addContract.tenantNameRequired');
      setSubmitError(message);
      showAlert({ type: 'error', title: message, message: '' });
      return;
    }
    if (!tenantPhone.trim()) {
      const message = isRequestMode
        ? 'Vui lòng nhập số điện thoại trước khi gửi chủ nhà.'
        : 'Vui lòng nhập số điện thoại người thuê trước khi tạo hợp đồng.';
      focusPhoneField(message);
      showAlert({
        type: 'error',
        title: 'Số điện thoại bắt buộc',
        message,
      });
      return;
    }
    if (!isRequestMode) {
      if (!startDate) {
        showAlert({ type: 'error', title: t('propertyListings.addContract.startDateRequired'), message: '' });
        return;
      }
      const today = getTodayForDateInput();
      if (startDate < today) {
        showAlert({
          type: 'error',
          title: 'Ngày bắt đầu không hợp lệ',
          message: 'Ngày bắt đầu phải từ hôm nay trở đi.',
        });
        return;
      }
    }
    const duration = parseInt(rentalDuration, 10);
    if (!duration || duration < 1) {
      showAlert({ type: 'error', title: t('propertyListings.addContract.durationRequired'), message: '' });
      return;
    }

    if (!isRequestMode && !activePropertyId) {
      showAlert({ type: 'error', title: 'Chọn phòng', message: 'Vui lòng chọn phòng/căn hộ để tạo hợp đồng.' });
      return;
    }

    const start = new Date(startDate);
    const endDate = new Date(start);
    endDate.setMonth(endDate.getMonth() + duration);

    try {
      setIsSubmitting(true);

      if (isRequestMode) {
        if (!payRoomCode?.trim()) {
          const message = 'Không xác định được mã QR phòng.';
          setSubmitError(message);
          showAlert({ type: 'error', title: 'Thiếu mã phòng', message });
          return;
        }
        const result = await roomPayApi.submitContractRequest(payRoomCode.trim(), {
          tenant_name: tenantName.trim(),
          tenant_phone: tenantPhone.trim(),
          tenant_cccd: cccdNumber.trim() || undefined,
          tenant_date_of_birth: dateOfBirth || undefined,
          tenant_address: address.trim() || undefined,
          rental_duration: duration,
        });
        showAlert({
          type: 'success',
          title: 'Đã gửi yêu cầu',
          message: result.message,
        });
        onRequestSubmitted?.();
        onClose();
        return;
      }

      let monthlyPrice = activePropertyPrice;
      try {
        const propertyResponse = await propertyApi.getPropertyById(activePropertyId.toString());
        if (propertyResponse.success && propertyResponse.data?.price) {
          monthlyPrice = Number(propertyResponse.data.price);
        }
      } catch {
        // Fallback to prop price if refresh fails
      }

      const createdContract = await contractApi.createContract({
        property_id: activePropertyId,
        contract_type: 'rental',
        start_date: startDate,
        end_date: endDate.toISOString().split('T')[0],
        amount: monthlyPrice * duration,
        monthly_payment: monthlyPrice,
        total_payments: duration,
        tenant_name: tenantName.trim(),
        tenant_cccd: cccdNumber.trim() || undefined,
        tenant_date_of_birth: dateOfBirth || undefined,
        tenant_address: address.trim() || undefined,
        tenant_phone: tenantPhone.trim() || undefined,
        notes: [
          cccdNumber.trim() ? `CCCD: ${cccdNumber.trim()}` : '',
          dateOfBirth ? `Ngày sinh: ${dateOfBirth}` : '',
          address.trim() ? `Địa chỉ: ${address.trim()}` : '',
          tenantPhone.trim() ? `SĐT: ${tenantPhone.trim()}` : '',
        ].filter(Boolean).join(' | '),
      });

      showAlert({
        type: 'success',
        title: isAcceptMode ? 'Đã chấp nhận yêu cầu' : t('propertyListings.addContract.success'),
        message: isAcceptMode
          ? 'Hợp đồng đã tạo. Mở mẫu để chủ nhà và khách thuê ký vào ô Bên cho thuê / Bên thuê.'
          : 'Đã tạo hợp đồng. Mở mẫu để hai bên ký trực tiếp vào cuối hợp đồng.',
      });
      onContractAdded?.(createdContract);
      onClose();

      if (createdContract?.id) {
        navigate(`/contracts/${createdContract.id}`);
      }
    } catch (error: unknown) {
      console.error('Error creating contract:', error);
      const message = requestErrorMessage(error);
      setSubmitError(message);
      showAlert({
        type: 'error',
        title: isRequestMode
          ? 'Không gửi được yêu cầu'
          : isAcceptMode
            ? 'Không thể chấp nhận yêu cầu'
            : t('propertyListings.addContract.error'),
        message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center bg-black/50 p-0 sm:p-4"
        onClick={onClose}
        role="presentation"
      >
        <div
          className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md max-h-[min(92dvh,calc(100vh-1rem))] sm:max-h-[min(88dvh,calc(100vh-2rem))] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-contract-modal-title"
        >
          <div className="shrink-0 border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2
                id="add-contract-modal-title"
                className="text-base sm:text-lg font-semibold text-black flex items-center gap-2"
              >
                <i className="fas fa-file-contract text-[#C4A35A] shrink-0"></i>
                <span className="truncate">{modalTitle}</span>
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">
                {isRequestMode
                  ? `${activePropertyLabel ? `${activePropertyLabel} — ` : ''}Quét QR CCCD để tự điền thông tin`
                  : isAcceptMode
                    ? 'Xác nhận thông tin và tạo hợp đồng'
                    : activePropertyLabel || 'Chọn phòng để tạo hợp đồng'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 text-gray-400 hover:text-black transition-colors p-1"
              aria-label="Close"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 sm:px-6 sm:py-4 space-y-3 sm:space-y-4">
              <div>
                <button
                  type="button"
                  onClick={handleOpenCccdScanner}
                  className="w-full rounded-xl border border-dashed border-[#C4A35A] bg-[#F7F1E3] px-3 py-2.5 text-sm font-medium text-[#8C5226] transition hover:bg-[#F0E4C5]"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <i className="fas fa-qrcode text-xs"></i>
                    Quét QR CCCD để tự động điền thông tin
                  </span>
                </button>
                {(tenantName || cccdNumber || dateOfBirth || address || tenantPhone) && (
                  <p className="mt-2 text-xs text-emerald-700">
                    <i className="fas fa-check-circle mr-1"></i>
                    Đã điền thông tin từ QR CCCD — kiểm tra và chỉnh sửa bên dưới nếu cần
                  </p>
                )}
              </div>

              {showPropertyPicker && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chọn phòng / căn hộ
                  </label>
                  <select
                    value={selectedPropertyId || ''}
                    onChange={(e) => setSelectedPropertyId(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/50 focus:border-[#C4A35A] bg-white"
                  >
                    <option value="">— Chọn phòng —</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {formatPropertyDisplayAddress(p)}
                        {' · '}
                        {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(p.price || 0)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {showPropertyPicker && properties.length === 0 && (
                <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
                  Không có phòng trống để tạo hợp đồng. Các phòng đang cho thuê đã có hợp đồng.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('propertyListings.addContract.tenantName')}
                </label>
                <input
                  type="text"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  placeholder={t('propertyListings.addContract.tenantNamePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/50 focus:border-[#C4A35A]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Số CCCD</label>
                  <input
                    type="text"
                    value={cccdNumber}
                    onChange={(e) => setCccdNumber(e.target.value)}
                    placeholder="Nhập số CCCD"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/50 focus:border-[#C4A35A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngày sinh</label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/50 focus:border-[#C4A35A]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Địa chỉ</label>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={2}
                  placeholder="Nhập địa chỉ thường trú"
                  className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/50 focus:border-[#C4A35A]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Số điện thoại <span className="text-red-500">*</span>
                </label>
                <input
                  ref={phoneInputRef}
                  type="tel"
                  value={tenantPhone}
                  onChange={(e) => {
                    setTenantPhone(e.target.value.replace(/[^\d+\s-]/g, ''));
                    if (submitError) setSubmitError(null);
                  }}
                  placeholder="Nhập số điện thoại người thuê"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/50 focus:border-[#C4A35A]"
                />
              </div>

              <div className={isRequestMode ? '' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('propertyListings.addContract.rentalDuration')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="120"
                      value={rentalDuration}
                      onChange={(e) => setRentalDuration(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/50 focus:border-[#C4A35A] pr-16"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                      {t('propertyListings.addContract.months')}
                    </span>
                  </div>
                </div>

                {!isRequestMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('propertyListings.addContract.startDate')}
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    min={getTodayForDateInput()}
                    onChange={(e) => {
                      const value = e.target.value;
                      const today = getTodayForDateInput();
                      setStartDate(value && value < today ? today : value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/50 focus:border-[#C4A35A]"
                  />
                </div>
                )}
              </div>
            </div>

            {submitError && (
              <p className="shrink-0 border-t border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700 sm:px-6">
                {submitError}
              </p>
            )}
            <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4 flex gap-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm sm:text-base"
              >
                {t('propertyListings.addContract.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || (showPropertyPicker && !selectedPropertyId)}
                className="flex-1 px-4 py-2.5 bg-[#C4A35A] text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin text-sm"></i>
                    {t('propertyListings.addContract.submitting')}
                  </>
                ) : (
                  <>
                    <i className={`fas ${submitIcon} text-sm`}></i>
                    {submitLabel}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <CccdScannerModal
        isOpen={isScannerOpen}
        onClose={handleCloseCccdScanner}
        onScanned={applyParsedCccd}
        autoOpenCamera={openNativeCameraOnScan}
        autoOpenGallery={openGalleryOnScan}
      />
    </>
  );
};

export default AddContractModal;
